import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Close from '@mui/icons-material/Close';
import { PARTIFY_PRESETS, MIN_PARTIFY_COLORS, MAX_PARTIFY_COLORS, isValidPartifyColor } from '../constants/partify_constants';

export default function PartifyColors({ preset, colors, onChange }) {
  const changeColor = (index, color) => onChange('custom', colors.map((value, i) => i === index ? color : value));
  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      <TextField
        id="partify-color-pattern"
        label="Color pattern"
        select
        fullWidth
        value={preset}
        slotProps={{ select: { native: true } }}
        onChange={event => {
          const selected = PARTIFY_PRESETS.find(item => item.id === event.target.value);
          onChange(event.target.value, selected ? [...selected.colors] : colors);
        }}
      >
        {PARTIFY_PRESETS.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
        <option value="custom">Custom</option>
      </TextField>
      <Box component="ol" aria-label="Color sequence" sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, listStyle: 'none', p: 0, my: 1 }}>
        {colors.map((color, index) => (
          <Box component="li" key={index} aria-label={`Color ${index + 1}: ${color}`} sx={{ width: 18, height: 18, borderRadius: '50%', border: '1px solid', borderColor: 'divider', bgcolor: isValidPartifyColor(color) ? color : 'transparent' }} />
        ))}
      </Box>
      <Typography variant="caption" component="p">Colors play from left to right.</Typography>
      {preset === 'custom' && (
        <Box sx={{ mt: 1 }}>
          {colors.map((color, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <input
                type="color"
                aria-label={`Pick color ${index + 1}`}
                value={isValidPartifyColor(color) ? color : '#000000'}
                onChange={event => changeColor(index, event.target.value.toUpperCase())}
                style={{ width: 36, height: 36, flexShrink: 0, padding: 0, border: 0, background: 'transparent' }}
              />
              <TextField
                id={`partify-color-${index}`}
                label={`Color ${index + 1}`}
                size="small"
                value={color}
                error={!isValidPartifyColor(color)}
                helperText={!isValidPartifyColor(color) ? 'Use #RRGGBB' : undefined}
                onChange={event => changeColor(index, event.target.value)}
                sx={{ minWidth: 0, flex: 1 }}
              />
              <IconButton
                aria-label={`Remove color ${index + 1}`}
                size="small"
                disabled={colors.length <= MIN_PARTIFY_COLORS}
                onClick={() => onChange('custom', colors.filter((_, i) => i !== index))}
              ><Close fontSize="small" /></IconButton>
            </Box>
          ))}
          <Button
            size="small"
            disabled={colors.length >= MAX_PARTIFY_COLORS}
            onClick={() => onChange('custom', [...colors, '#FFFFFF'])}
          >Add color</Button>
          <Typography variant="caption" component="p">Choose 2–12 colors.</Typography>
        </Box>
      )}
    </Box>
  );
}

PartifyColors.propTypes = {
  preset: PropTypes.string.isRequired,
  colors: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
};
